import {Box, Checkbox, IconButton, Menu, Text} from '@chakra-ui/react'
import {PatientTherapySequenceStatus} from '@cypro/engine/database/schema'
import {PatientProtocol} from '@cypro/engine/queries'
import {computeCycleDayFromTherapyDate} from '@cypro/engine/utils'
import {dialog} from '@cypro/ui/components'
import {toaster} from '@cypro/ui/toaster'
import {getShortDateFormatter} from '@cypro/ui/utils'
import {useCan} from '@/shared/hooks/use-permissions'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'
import {
  LuCalendar1,
  LuCircleStop,
  LuRotateCcw,
  LuEllipsisVertical,
  LuPen,
  LuStethoscope,
  LuTrash2,
} from 'react-icons/lu'
import {useGetPatientTherapies} from '@/modules/patients/api/use-get-patient-therapies'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {usePatientTherapyContext} from '@/shared/contexts'
import {CircleProgress} from '@/shared/components/ui/circle-progress'
import {EditProtocolDialog} from './dialogs/edit-protocol'
import {RemoveProtocolDialog} from './dialogs/remove-protocol-dialog'
import {StopProtocolDialog} from './dialogs/stop-protocol-dialog'

interface ProtocolCardProps {
  patientProtocol: PatientProtocol
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}

export function ProtocolCard({patientProtocol, checked, onCheckedChange}: ProtocolCardProps) {
  const {t} = useTranslate('common')
  const canEditPrescriptions = useCan('prescriptions.edit')
  const zero = useZero()
  const {isPendingHl7Import} = usePatientTherapyContext()
  const [patientTherapies] = useGetPatientTherapies(patientProtocol.patientId)
  const protocol = patientProtocol?.protocol

  const protocolSequences = useMemo(
    () =>
      patientTherapies.filter(
        seq =>
          seq.patientProtocolId === patientProtocol.id &&
          !seq.isNote &&
          seq.status !== PatientTherapySequenceStatus.enum.cancelled
      ),
    [patientTherapies, patientProtocol.id]
  )

  function onReactivate() {
    void zero.attemptMutation(
      zero.mutators.patients.reactivateProtocol({patientProtocolId: patientProtocol.id}),
      async mutation => {
        const data = await mutation.server
        if (!data) return
        toaster.success({title: t('success'), description: t('patient_updated_successfully')})
      }
    )
  }

  if (!protocol) {
    return null
  }
  const diagnosisName = protocol.diagnosis?.name
  const startDate = new Date(patientProtocol.startDate)
  const endDate = patientProtocol.endDate ? new Date(patientProtocol.endDate) : null
  const now = new Date().getTime()
  let protocolEnded = false
  if (endDate) {
    const endOfEndDate = new Date(endDate)
    endOfEndDate.setHours(23, 59, 59, 999)
    protocolEnded = now > endOfEndDate.getTime()
  }
  const totalCycles = (patientProtocol.cycles ?? protocol.totalCycles) || 0
  const daysRepetition = protocol.daysRepetition || 0
  const lastCycle = Math.max(totalCycles, 1)
  const currentCycle = protocolEnded
    ? lastCycle
    : startDate.getTime() > now
      ? 1
      : Math.min(
          Math.max(computeCycleDayFromTherapyDate(patientProtocol, now, daysRepetition, false).cycle, 1),
          lastCycle
        )

  // Progress within the current cycle: administered prescriptions out of all its prescriptions.
  const cycleSequences = protocolSequences.filter(seq => seq.cycle === currentCycle)
  const administeredCount = cycleSequences.filter(
    seq => seq.status === PatientTherapySequenceStatus.enum.administered
  ).length

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      borderRadius={'md'}
      border={'solid'}
      bg={checked ? 'bg.subtle' : 'bg.panel'}
      borderWidth={'1px'}
      borderColor={checked ? 'gray.focusRing' : 'border'}
      minWidth="18rem"
      maxWidth="28rem"
      height="6.75rem"
      gap={'2'}
      p="2"
    >
      <Box display={'flex'} flexDirection={'column'} gap={'2'}>
        <Box width="100%" height="20px" display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
          <Box display="flex" gap="2" alignItems="center">
            <CircleProgress
              progress={administeredCount}
              max={cycleSequences.length || 1}
              color={protocolEnded ? 'orange' : 'green'}
            />
            <Text fontSize={'xs'} fontWeight={'medium'}>
              {t('protocol.cycle_of', {
                currentCycle,
                totalCycles: totalCycles || '-',
                days: daysRepetition,
              })}
            </Text>
          </Box>

          <Box display="flex" gap="1" alignItems="center">
            <Checkbox.Root checked={checked} onCheckedChange={e => onCheckedChange(!!e.checked)}>
              <Checkbox.HiddenInput />
              <Checkbox.Control>
                <Checkbox.Indicator cursor={'pointer'} />
              </Checkbox.Control>
              <Checkbox.Label />
            </Checkbox.Root>
            {canEditPrescriptions && (
              <Menu.Root>
                <Menu.Trigger asChild>
                  <IconButton size="sm" variant="ghost" title={t('more_actions')}>
                    <LuEllipsisVertical />
                  </IconButton>
                </Menu.Trigger>
                <Menu.Positioner>
                  <Menu.Content>
                    <Menu.Item
                      value="edit"
                      disabled={Boolean(patientProtocol.endDate) || isPendingHl7Import}
                      onSelect={() => {
                        if (patientProtocol.endDate || isPendingHl7Import) return
                        void dialog.open(`edit-patient-protocol-${patientProtocol.id}`, {
                          children: <EditProtocolDialog patientProtocol={patientProtocol} />,
                          contentProps: {minWidth: '50vw'},
                        })
                      }}
                    >
                      <LuPen />
                      {t('edit')}
                    </Menu.Item>
                    {patientProtocol.endDate ? (
                      <Menu.Item value="reactivate" onSelect={onReactivate}>
                        <LuRotateCcw />
                        {t('reactivate', 'Reactivate')}
                      </Menu.Item>
                    ) : (
                      <Menu.Item
                        value="stop"
                        onSelect={() => {
                          void dialog.open(`stop-patient-protocol-${patientProtocol.id}`, {
                            children: <StopProtocolDialog patientProtocol={patientProtocol} />,
                            contentProps: {minWidth: '28vw'},
                          })
                        }}
                      >
                        <LuCircleStop />
                        {t('stop', 'Stop')}
                      </Menu.Item>
                    )}
                    <Menu.Item
                      value="remove"
                      color="red.fg"
                      onSelect={() => {
                        void dialog.open(`remove-patient-protocol-${patientProtocol.id}`, {
                          children: <RemoveProtocolDialog patientProtocol={patientProtocol} />,
                          contentProps: {minWidth: '28vw'},
                        })
                      }}
                    >
                      <LuTrash2 />
                      {t('remove_protocol', 'Remove protocol')}
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Menu.Root>
            )}
          </Box>
        </Box>
        <Box width="100%" display={'flex'} flexDirection={'column'} alignItems={'flex-start'}>
          <Text
            fontSize="sm"
            width="100%"
            fontWeight="medium"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            overflow="hidden"
            lineHeight="20px"
          >
            {protocol?.name || '-'}
          </Text>
          <Text
            fontSize="sm"
            width="100%"
            fontWeight="normal"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            overflow="hidden"
            lineHeight="20px"
          >
            {diagnosisName || '-'}
          </Text>
        </Box>
      </Box>
      <Box display={'flex'} flexDirection={'row'} gap={'2'}>
        <Box
          display="flex"
          gap="2"
          alignItems="center"
          bg="gray.subtle"
          shadow="md"
          maxHeight="1rem"
          borderRadius="md"
          paddingInline="1"
        >
          <LuCalendar1 size="0.625rem" />
          <Text fontSize={'2xs'} fontWeight={'normal'}>
            {getShortDateFormatter().format(startDate)}
            {endDate ? ` - ${getShortDateFormatter().format(endDate)}` : ''}
          </Text>
        </Box>
        {patientProtocol.created?.displayUsername && (
          <Box
            display="flex"
            gap="2"
            alignItems="center"
            bg="cyan.subtle"
            shadow="md"
            shadowColor="cyan.fg"
            maxHeight="1rem"
            borderRadius="md"
            paddingInline="1"
            colorPalette="cyan"
            paddingBlock="0"
          >
            <LuStethoscope size="0.625rem" color="cyan.fg" />
            <Text fontSize={'2xs'} fontWeight={'normal'} color="cyan.fg">
              {patientProtocol.created?.displayUsername}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  )
}
